// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { MathUtils } from '@shared/utils/MathUtils';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import TerceraQuantityNumericTemplate from '@front/templates/TerceraQuantityNumericTemplate.html';
import { TypesManagerScreen } from '../screen/TypesManagerScreen';
import { ContainerControl } from './ContainerControl';
import { TerceraNumericEvents } from './TerceraNumeric';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { ControlsTypes } from '../UtilsClasses/FactoryConstants';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { WDSettings } from '../settings/WDGeneralSettings';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class TerceraQuantityNumeric extends ContainerControl {
    constructor() {
        super();
    }
    getType() {
        return ControlsTypes.TerceraQuantityNumeric;
    }
    oninit() {
        super.oninit();
        this.on('openQuantityMenu', this.openQuantityMenu);
        // TODO. Ugly. Remove defaultQuantity.
        this.observe('productType', this.UpdateProductType);
        this.observe('defaultQuantity', this.UpdateDefaultQuantity);
        this.observe('instrument', this.UpdateInstrument);
        this.observe('account', this.UpdateAccount);
        this.observe('value', this.onValueChanged);
        this.observe('assetNameForCash', this.UpdateRiskSettings); // to recreate the default quantity dropdown
    }
    oncomplete() {
        super.oncomplete();
        WDSettings.settingsChanged.Subscribe(this.UpdateRiskSettings, this);
        WDSettings.insDefSettingsStorage.DefaultSettingsChanged.Subscribe(this.UpdateRiskSettings, this);
    }
    dispose() {
        WDSettings.settingsChanged.UnSubscribe(this.UpdateRiskSettings, this);
        WDSettings.insDefSettingsStorage.DefaultSettingsChanged.UnSubscribe(this.UpdateRiskSettings, this);
        const ins = this.get('instrument');
        if (!isNullOrUndefined(ins)) {
            ins.RiskSettingsUpdated.UnSubscribe(this.UpdateRiskSettings, this);
        }
        super.dispose();
    }
    UpdateInstrument(n, o) {
        if (!isNullOrUndefined(o)) {
            o.RiskSettingsUpdated.UnSubscribe(this.UpdateRiskSettings, this);
        }
        if (!isNullOrUndefined(n)) {
            n.RiskSettingsUpdated.Subscribe(this.UpdateRiskSettings, this);
        }
        this.settingsUpdate();
    }
    UpdateDefaultQuantity() {
        this.settingsUpdate();
    }
    UpdateRiskSettings() {
        this.settingsUpdate(true);
    }
    UpdateProductType(n, o) {
        this.settingsUpdate(!isNullOrUndefined(o));
    }
    UpdateAccount(n, o) {
        this.settingsUpdate();
    }
    settingsUpdate(restoreQty = false) {
        var _a;
        const account = this.get('account');
        const ins = this.get('instrument');
        if (isNullOrUndefined(ins)) {
            return;
        }
        const prevValue = this.get('value');
        const prevInLots = this.get('inLots');
        const inLots = WDSettingsUtils.displayAmountInLots();
        void this.set('inLots', inLots);
        const selectedProductType = (_a = this.get('productType')) !== null && _a !== void 0 ? _a : ProductType.General;
        const qtyNumericSettings = this.isCashMode
            ? Quantity.getCashNumericSettings(ins, account)
            : Quantity.getQuantityNumericSettings(ins, account, inLots, selectedProductType);
        // TODO. Ugly. Remove.
        const defaultQuantity = this.get('defaultQuantity');
        if (defaultQuantity) {
            qtyNumericSettings.value = Quantity.convertQuantityValue(defaultQuantity, ins, inLots, account, selectedProductType);
        }
        if (restoreQty) {
            if (this.isCashMode) {
                qtyNumericSettings.value = prevValue;
            }
            else {
                qtyNumericSettings.value = Quantity.convertQuantityValue(new Quantity(prevValue, prevInLots), ins, inLots, account, selectedProductType);
            }
        }
        void this.set({
            minValue: qtyNumericSettings.minValue,
            maxValue: qtyNumericSettings.maxValue,
            step: qtyNumericSettings.step,
            decimalPrecision: qtyNumericSettings.decimalPrecision,
            value: qtyNumericSettings.value,
            incrementCoefficient: qtyNumericSettings.incrementCoefficient
        });
        this.createDefaultLotsDropDown();
    }
    onValueChanged(value) {
        this.fire(TerceraNumericEvents.ValueChanged, value);
    }
    openQuantityMenu(context) {
        if (!this.get('enabled')) {
            return;
        }
        if (this.get('isQuantityMenuOpen')) {
            hideContextMenu();
            return;
        }
        const control = context.element.find('.js-popup-button');
        const rect = control.getBoundingClientRect();
        showContextMenu({
            x: rect.x,
            y: rect.y + rect.height,
            items: this.menuItems,
            functions: {
                onContextMenuInitialized: () => {
                    this.setFocus();
                    void this.set('isQuantityMenuOpen', true);
                },
                closeCallback: () => {
                    this.onQuantityMenuClosed();
                },
                onOutsideClick: (event) => {
                    if (!rect.contains(event.x, event.y)) {
                        hideContextMenu();
                    }
                }
            }
        });
    }
    setFocus() {
        super.setFocus();
        this.Controls.QTYNumeric.setFocus();
    }
    createDefaultLotsDropDown() {
        const menuItems = [];
        if (!this.isCashMode) {
            const ins = this.get('instrument');
            this.addDefaultLots(menuItems, ins);
        }
        this.addEditButton(menuItems);
        this.menuItems = menuItems;
    }
    onDropdownValueSelected(menuItem) {
        const value = menuItem.tag;
        void this.set('value', value);
    }
    onEditClicked() {
        TypesManagerScreen.show(this, this.get('instrument'), TerceraQuantityNumeric.typesManagerScreenCallBack);
    }
    static typesManagerScreenCallBack(settings) {
        SessionSettings.updateInstrumentsDefaultSettings(settings);
    }
    // For TypesManagerScreen.
    getAllInstrumentsDefaultSettings() {
        return WDSettings.insDefSettingsStorage.GetSettingsCopy();
    }
    static sortLots(a, b) {
        return a - b;
    }
    get isCashMode() {
        return this.get('assetNameForCash') !== '';
    }
    addDefaultLots(menuItems, ins) {
        // Getting instrument's defaults.
        const insDefaults = WDSettings.insDefSettingsStorage.GetInstrumentSettings(ins);
        if (isNullOrUndefined(insDefaults)) {
            return;
        }
        // Populating dropdown by instrument's default lots values.
        const sortedLots = [];
        // Set of doubles.
        const lotValues = {};
        const defLots = insDefaults.DefaultLots;
        const defLotsLen = defLots.length;
        for (let i = 0; i < defLotsLen; i++) {
            const selectiveLot = defLots[i];
            const selectiveLotVal = selectiveLot.Lots;
            if (!selectiveLot.Selected || lotValues[selectiveLotVal]) {
                continue;
            }
            lotValues[selectiveLotVal] = true;
            sortedLots.push(selectiveLotVal);
        }
        sortedLots.sort(TerceraQuantityNumeric.sortLots);
        const strItemsList = [];
        const decimalPrecision = this.get('decimalPrecision');
        const divider = Math.pow(10, decimalPrecision);
        // #37103 - возможна такая ситуация, когда значения default lots меньше чем Lot step,
        // поэтому мы здесь удаляем дубликаты, если необходимо
        const sortedLotsLen = sortedLots.length;
        for (let i = 0; i < sortedLotsLen; i++) {
            let convValue = sortedLots[i];
            if (!WDSettings.isQuantityInLots) {
                convValue *= ins.LotSize;
            }
            // обрезаем без округления дробную часть числа, которая выходит за пределы DecimalPlaces
            let dConvValue = MathUtils.trunc(convValue * divider) / divider;
            // Js float calc fix.
            dConvValue = parseFloat(dConvValue.toFixed(decimalPrecision));
            const strConvValue = MathUtils.formatValueWithEps(InstrumentUtils.formatAmountValue(convValue, ins));
            if (!strItemsList.includes(strConvValue) && dConvValue !== 0) {
                strItemsList.push(strConvValue);
                menuItems.push({
                    type: NewContextMenuItemType.Default,
                    text: strConvValue,
                    tag: dConvValue,
                    event: this.onDropdownValueSelected.bind(this)
                });
            }
        }
    }
    addEditButton(menuItems) {
        if (menuItems.length > 0) {
            menuItems.push({ type: NewContextMenuItemType.Separator });
        }
        menuItems.push({
            type: NewContextMenuItemType.Default,
            text: 'NumericComboCtrl.Edit',
            event: this.onEditClicked.bind(this)
        });
    }
    onQuantityMenuClosed() {
        void this.set('isQuantityMenuOpen', false);
        this.lostFocus();
    }
}
ContainerControl.extendWith(TerceraQuantityNumeric, {
    template: TerceraQuantityNumericTemplate,
    data: function () {
        return {
            minValue: null,
            maxValue: null,
            isPosAbsolute: false,
            account: null,
            instrument: null,
            inLots: false,
            height: '',
            // TODO. Ugly. Remove.
            defaultQuantity: null,
            noArrowCB: false,
            showArrows: true,
            assetNameForCash: '', // #122525
            isQuantityMenuOpen: false
        };
    }
});
